import { getAuth, signOut } from 'firebase/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://library-admin-478409.et.r.appspot.com/api';
const LOCAL_QA_SESSION_KEY = 'localAdminQaSession';

/**
 * One refresh at a time. A console left open past the staff token's one-day
 * expiry fires several requests at once when it wakes up; without this pair
 * every one of them would run its own /admin/auth exchange and the last one to
 * finish would win. They now all wait on the same promise.
 */
let isRefreshing = false;
let refreshPromise = null;

class ApiService {
    constructor() {
        this.baseURL = API_BASE_URL;
    }

    getAuthHeaders() {
        const token = localStorage.getItem('adminAccessToken');
        return {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
        };
    }

    /**
     * Force a fresh Firebase ID token and trade it for a new staff JWT — the
     * same silent refresh the CRM has had all along. Returns false rather than
     * throwing, so the caller decides whether a failure means "sign out".
     */
    async refreshAccessToken() {
        if (isRefreshing) return refreshPromise;

        isRefreshing = true;
        refreshPromise = (async () => {
            try {
                const currentUser = getAuth().currentUser;
                if (!currentUser) return false;

                const idToken = await currentUser.getIdToken(true);
                const response = await fetch(`${this.baseURL}/admin/auth`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ idToken }),
                });

                const data = await response.json().catch(() => null);
                if (!response.ok || !data?.accessToken) return false;

                localStorage.setItem('adminAccessToken', data.accessToken);
                return true;
            } catch (error) {
                return false;
            } finally {
                isRefreshing = false;
            }
        })();

        return refreshPromise;
    }

    /**
     * Only reached when the refresh itself failed — the Firebase session is
     * gone, or the account is no longer an active team member. There is no
     * /signin route here: signing out of Firebase is what makes App render the
     * sign-in screen.
     */
    async endSession() {
        localStorage.removeItem('adminAccessToken');
        localStorage.removeItem(LOCAL_QA_SESSION_KEY);
        try {
            await signOut(getAuth());
        } catch (error) {
            // The reload below is what actually returns the user to sign-in.
        }
        window.location.replace('/');
    }

    async request(endpoint, options = {}, allowRefresh = true) {
        const url = `${this.baseURL}${endpoint}`;
        const config = {
            headers: this.getAuthHeaders(),
            ...options,
        };

        try {
            const response = await fetch(url, config);

            // Retry once behind a fresh token. /admin/auth is excluded or a
            // rejected exchange would try to refresh itself in a loop.
            if (response.status === 401 && allowRefresh && !endpoint.startsWith('/admin/auth')) {
                const refreshed = await this.refreshAccessToken();
                if (refreshed) return this.request(endpoint, options, false);

                await this.endSession();
                throw new Error('Your session has expired. Please sign in again.');
            }

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || `HTTP error! status: ${response.status}`);
            }

            return data;
        } catch (error) {
            console.error('API request failed:', error);
            throw error;
        }
    }

    async get(endpoint) {
        return this.request(endpoint);
    }

    async post(endpoint, data) {
        return this.request(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async patch(endpoint, data) {
        return this.request(endpoint, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async delete(endpoint) {
        return this.request(endpoint, {
            method: 'DELETE',
        });
    }
}

export const api = new ApiService();
